/**
 * AdminOffers — Mandanten-weite Angebots-Übersicht.
 *
 * Suche nach Angebotsnummer, Kunde, Firma. Filter nach Status + Datum.
 * Klick → öffnet das zugehörige Projekt (Tab Angebote).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, RefreshCw, FileText, Loader2, Calendar, Filter, ExternalLink, Building2, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import { EasybillPushButton } from "@/components/EasybillPushButton";

const STATUS_LABEL = {
  o_angebot_versendet: {
    label: "O-Angebot versendet",
    cls: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
  },
  draft:    { label: "Entwurf",  cls: "bg-zinc-700 text-zinc-200" },
  sent:     { label: "Versendet", cls: "bg-sky-500/15 text-sky-300 border border-sky-500/30" },
  signed:   { label: "Unterschrieben", cls: "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40" },
  cancelled:{ label: "Storniert", cls: "bg-rose-500/15 text-rose-300 border border-rose-500/30" },
};

const fmtEur = (n) =>
  Number.isFinite(Number(n))
    ? `${Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : "—";
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return "—"; }
};

export default function AdminOffers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");

  const deleteOffer = async (o) => {
    // Zwei-Klick-Bestätigung (window.confirm wird in manchen Browsern blockiert):
    // 1. Klick → Zeile wechselt in "Bestätigen"-Zustand (7 Sek. sichtbar)
    // 2. Klick auf denselben Button → wirklich löschen
    if (confirmDeleteId !== o.id) {
      if (o.status === "signed") {
        toast.error("Unterschriebene Angebote können nicht gelöscht werden.");
        return;
      }
      setConfirmDeleteId(o.id);
      toast.warning(`Angebot ${o.offer_number} löschen?`, {
        description: `${o.customer_name || "—"} · ${o.total_gross ? Number(o.total_gross).toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €" : "—"} · Zum Bestätigen nochmal auf den Papierkorb klicken.`,
        duration: 7000,
      });
      setTimeout(() => setConfirmDeleteId((cur) => (cur === o.id ? "" : cur)), 7000);
      return;
    }
    setConfirmDeleteId("");
    setDeletingId(o.id);
    try {
      await api.delete(`/offers/${o.id}`);
      setOffers((arr) => arr.filter((x) => x.id !== o.id));
      toast.success(`Angebot ${o.offer_number} gelöscht`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setDeletingId(""); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/offers", { params });
      setOffers(data.offers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, dateFrom, dateTo]);

  const summary = useMemo(() => {
    const total = offers.length;
    const sentOrSigned = offers.filter((o) => ["sent", "signed"].includes(o.status)).length;
    const sumGross = offers.reduce((acc, o) => acc + (Number(o.total_gross) || 0), 0);
    return { total, sentOrSigned, sumGross };
  }, [offers]);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-offers-page">
      <AppHeader title="Angebote — Übersicht" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 pb-12">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            <FileText className="text-[#00E575]" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Angebote</h1>
            <p className="text-zinc-400 text-[12px] font-medium">Mandanten-weite Suche und Statusübersicht.</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="offers-refresh"
            className="h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40"
            title="Neu laden"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-zinc-400" : "text-zinc-400"} />
          </button>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <div className="min-w-0 bg-[#18181B] border border-zinc-800 rounded-xl px-3 py-2.5" data-testid="offers-kpi-total">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-200 font-bold">Treffer</div>
            <div className="text-[20px] font-bold text-zinc-100 font-mono">{summary.total}</div>
          </div>
          <div className="min-w-0 bg-[#18181B] border border-sky-500/30 rounded-xl px-3 py-2.5" data-testid="offers-kpi-sent">
            <div className="text-[10.5px] uppercase tracking-wider text-zinc-200 font-bold break-words">Versendet/ Signiert</div>
            <div className="text-[20px] font-bold text-sky-300 font-mono">{summary.sentOrSigned}</div>
          </div>
          <div className="col-span-2 sm:col-span-1 min-w-0 bg-[#18181B] border border-[#00E575]/30 rounded-xl px-3 py-2.5" data-testid="offers-kpi-volume">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-200 font-bold">Volumen (brutto)</div>
            <div className="text-[18px] sm:text-[20px] font-bold text-[#00E575] font-mono truncate">{fmtEur(summary.sumGross)}</div>
          </div>
        </div>

        {/* Filterleiste */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="sm:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="offers-search"
              placeholder="Suche: Angebotsnummer, Name, Firma…"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg pl-9 pr-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px]"
            />
          </div>
          <div className="relative">
            <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="offers-status"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg pl-8 pr-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px]"
            >
              <option value="">Alle Status</option>
              <option value="o_angebot_versendet">O-Angebot versendet</option>
              <option value="draft">Entwurf</option>
              <option value="sent">Versendet</option>
              <option value="signed">Unterschrieben</option>
              <option value="cancelled">Storniert</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <Calendar size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="offers-date-from"
                title="Von Datum"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg pl-7 pr-2 text-zinc-50 focus:border-[#00E575] outline-none text-[12px] font-medium"
              />
            </div>
            <div className="relative">
              <Calendar size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="offers-date-to"
                title="Bis Datum"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg pl-7 pr-2 text-zinc-50 focus:border-[#00E575] outline-none text-[12px] font-medium"
              />
            </div>
          </div>
        </div>

        {/* Tabelle */}
        {loading ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center">
            <Loader2 className="animate-spin text-zinc-400" size={22} />
          </div>
        ) : offers.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Keine Angebote gefunden. Filter anpassen oder Suche leeren.
          </div>
        ) : (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="offers-table">
                <thead>
                  <tr className="text-left text-zinc-400 uppercase text-[12px] tracking-widest border-b border-zinc-800">
                    <th className="px-3 py-2.5 font-bold">Nummer</th>
                    <th className="px-3 py-2.5 font-bold">Datum</th>
                    <th className="px-3 py-2.5 font-bold">Kunde</th>
                    <th className="px-3 py-2.5 font-bold">Projekt</th>
                    <th className="px-3 py-2.5 font-bold">Zugewiesen an</th>
                    <th className="px-3 py-2.5 font-bold">Status</th>
                    <th className="px-3 py-2.5 font-bold text-right">Betrag (brutto)</th>
                    <th className="px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((o) => {
                    const st = STATUS_LABEL[o.status] || { label: o.status || "—", cls: "bg-zinc-800 text-zinc-300" };
                    const isStub = !o.project_id;
                    const rowClick = () => {
                      if (o.project_id) {
                        navigate(`/projects/${o.project_id}`);
                      } else if (o.source_lead_id) {
                        // O-Angebot ohne Projekt → passendes Ziel je nach Rolle
                        const telesalesRoles = ["agent", "admin", "telesales_director"];
                        if (telesalesRoles.includes(role)) {
                          navigate(`/telesales?tab=submissions&lead=${o.source_lead_id}`);
                        } else {
                          // sales_director / sales_manager: Admin-Leads-Übersicht
                          navigate(`/admin/orientierungsangebote?lead=${o.source_lead_id}`);
                        }
                      }
                    };
                    return (
                      <tr
                        key={o.id}
                        data-testid={`offer-row-${o.offer_number}`}
                        className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 cursor-pointer transition"
                        onClick={rowClick}
                      >
                        <td className="px-3 py-2.5 font-mono font-bold text-[#00E575]">
                          {o.offer_number || "—"}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-300">{fmtDate(o.created_at)}</td>
                        <td className="px-3 py-2.5">
                          <div className="font-semibold text-zinc-100">{o.customer_name || "—"}</div>
                          {o.customer_company && (
                            <div className="text-[10.5px] text-zinc-500 flex items-center gap-1">
                              <Building2 size={9} /> {o.customer_company}
                            </div>
                          )}
                          {(o.customer_zip || o.customer_city) && (
                            <div className="text-[10.5px] text-zinc-500">
                              {o.customer_zip} {o.customer_city}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-400 font-mono text-[12px]">
                          {o.project_number || (isStub ? <span className="text-amber-400/80 italic">— (offen)</span> : "—")}
                        </td>
                        <td className="px-3 py-2.5" data-testid={`offer-assignee-${o.offer_number}`}>
                          {o.assigned_user_name ? (
                            <div>
                              <div className="text-zinc-100 text-[12px] font-medium">{o.assigned_user_name}</div>
                              {o.assigned_user_role && (
                                <div className="text-[12px] text-zinc-300 uppercase tracking-widest">{o.assigned_user_role}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-zinc-300 italic text-[12px]">— nicht zugewiesen</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[12px] uppercase tracking-widest font-bold ${st.cls}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-zinc-100">
                          {fmtEur(o.total_gross)}
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <EasybillPushButton offerId={o.id} compact />
                            <button
                              type="button"
                              data-testid={`offer-delete-${o.offer_number}`}
                              onClick={(e) => { e.stopPropagation(); deleteOffer(o); }}
                              disabled={deletingId === o.id}
                              title={confirmDeleteId === o.id ? "Nochmal klicken zum Löschen" : "Angebot löschen"}
                              className={`h-7 w-7 rounded-md flex items-center justify-center transition disabled:opacity-50 ${
                                confirmDeleteId === o.id
                                  ? "bg-rose-500 text-white animate-pulse"
                                  : "hover:bg-rose-500/10 hover:text-rose-300 text-zinc-500"
                              }`}
                            >
                              {deletingId === o.id
                                ? <Loader2 size={13} className="animate-spin" />
                                : <Trash2 size={13} />}
                            </button>
                            <ExternalLink size={13} className="text-zinc-500" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[10.5px] text-zinc-500 mt-3 leading-relaxed">
          Klick auf eine Zeile öffnet die Projektakte (Tab &bdquo;Angebote&ldquo;) — dort sehen Sie das vollständige Angebot inkl. PDF-Vorschau, Positionen, Provisionen und Versand-Optionen.
        </p>
      </main>
    </div>
  );
}
